import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Network, Layers } from "lucide-react";
import { useDemo } from "@/demo/DemoProvider";
import { useAuth } from "@/auth/AuthProvider";
import {
  useEntitlement,
  useStartCheckout,
  pollCheckoutStatus,
} from "@/lib/checkout";
import PWAInstallButton from "@/components/PWAInstallButton";

/**
 * Promotional landing page at /welcome. The pitch surface. Hero copy
 * is intentionally locked verbatim to the founder's wording — the rest
 * of the page presents the three core lenses (decomposition / phonetic
 * atlas / radical trees) as discrete "pillars" so the value prop is
 * legible in a 30-second scroll. The "Take the tour" CTA fires the
 * locked guided demo which uses the real product.
 *
 * Design language follows the rest of the app — washi paper, sumi ink,
 * shu vermilion. No stock photography; every visual is real kanji
 * rendered with the same fonts the app uses. Restrained motion (fade
 * + small y) keeps it feeling like a printed press release more than
 * a SaaS landing page.
 */
export default function Welcome() {
  const navigate = useNavigate();
  const demo = useDemo();
  const { user, signIn } = useAuth();
  const { lifetime, refetch: refetchEntitlement } = useEntitlement();
  const startCheckout = useStartCheckout();
  const [params, setParams] = useSearchParams();
  // Possible values: "idle" | "polling" | "paid" | "cancelled" | "failed"
  const [pay, setPay] = useState("idle");

  // Paid + Google-authenticated → no need to look at the marketing
  // page on cold launch; shortcut them straight to the app. The flag
  // here suppresses Welcome's regular UI for a frame while we
  // navigate, avoiding a flash of marketing copy.
  useEffect(() => {
    if (!lifetime || !user) return;
    if (pay !== "idle") return;  // mid-checkout flow — let it finish
    try { localStorage.setItem("kb_seen_app_v2", "1"); } catch (e) {
      console.warn("kb_seen_app_v2 persist failed (private browsing?)", e);
    }
    navigate("/", { replace: true });
  }, [lifetime, user, pay, navigate]);

  // ── Stripe return handler ────────────────────────────────────────
  // When Stripe redirects us back with ?session_id=… we poll the
  // backend until the row is "paid", then refresh the entitlement.
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("checkout") === "cancelled";
    if (cancelled) {
      setPay("cancelled");
      params.delete("checkout");
      setParams(params, { replace: true });
      return;
    }
    if (!sid) return;
    setPay("polling");
    (async () => {
      const r = await pollCheckoutStatus(sid);
      if (r.ok) {
        setPay("paid");
        await refetchEntitlement();
      } else {
        setPay("failed");
      }
      params.delete("session_id");
      setParams(params, { replace: true });
    })();
  }, [params, setParams, refetchEntitlement]);

  const startDemo = () => {
    // The demo navigates to "/" on its second beat, but the
    // FirstVisitRedirect guard would bounce that back to /welcome unless
    // we mark the app as "seen". Setting the flag here is the right
    // moment — the user has chosen to engage with the product.
    try { localStorage.setItem("kb_seen_app_v2", "1"); } catch (e) {
      console.warn("kb_seen_app_v2 persist failed (private browsing?)", e);
    }
    navigate("/");
    setTimeout(() => demo?.controls?.start?.(), 80);
  };

  return (
    <div className="min-h-screen bg-washi text-sumi relative overflow-hidden" data-testid="welcome-page">
      {/* ── Top sign-in strip for subscribers who haven't logged in here ── */}
      {lifetime && !user && (
        <div
          data-testid="welcome-paid-signin-strip"
          className="bg-washi border-b border-shu/30 px-4 py-2 flex items-center justify-center gap-4 flex-wrap"
        >
          <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumi">
            lifetime member
          </span>
          <button
            type="button"
            onClick={signIn}
            data-testid="welcome-paid-signin-top"
            className="font-sans text-[11px] uppercase tracking-[0.25em] text-shu hover:text-sumi underline-offset-4 hover:underline transition-colors"
          >
            login with google →
          </button>
        </div>
      )}
      {/* ── Top status strip for signed-in users who haven't bought yet ──
          Without this they hit the marketing page after Google login and
          can't tell whether anything happened. We make their auth state
          visible AND give them a one-tap exit to the in-app demo so they
          aren't trapped behind the paywall. */}
      {user && !lifetime && pay === "idle" && (
        <div
          data-testid="welcome-signed-in-strip"
          className="bg-washi border-b border-shu/30 px-4 py-2 flex items-center justify-center gap-4 flex-wrap"
        >
          <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumi">
            signed in as {user.email || user.name || "you"}
          </span>
          <a
            href="#welcome-pricing"
            data-testid="welcome-signed-in-jump-pricing"
            className="font-sans text-[11px] uppercase tracking-[0.25em] text-shu hover:text-sumi underline-offset-4 hover:underline transition-colors"
          >
            unlock for £20 →
          </a>
        </div>
      )}
      {/* ── Stripe return banner (transient — only during/right after checkout) ── */}
      {pay !== "idle" && !lifetime && (
        <div
          data-testid="welcome-checkout-banner"
          className={`text-center py-3 px-4 font-sans text-sm uppercase tracking-[0.25em] ${
            pay === "polling"
              ? "bg-sumi/90 text-washi"
              : "bg-sumiSoft/20 text-sumi"
          }`}
        >
          {pay === "polling" && "Checking your payment…"}
          {pay === "cancelled" && "Checkout was cancelled. The demo is still available."}
          {pay === "failed" && "Couldn't confirm payment — please check your email or try again."}
        </div>
      )}
      {/* ── Subtle washi-paper grain (SVG noise, very low opacity) ─── */}
      <svg
        aria-hidden
        className="pointer-events-none fixed inset-0 w-full h-full opacity-[0.035] mix-blend-multiply"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="welcome-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 0.13  0 0 0 0 0.10  0 0 0 0 0.08  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#welcome-grain)" />
      </svg>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-16 md:pb-24 relative">
        {/* Vertical brush-stroke accent on the left margin */}
        <span
          aria-hidden
          className="hidden md:block absolute left-6 lg:left-10 top-20 bottom-20 w-px bg-gradient-to-b from-transparent via-sumi/15 to-transparent pointer-events-none"
        />
        {/* Vertical reading column — quiet Japanese touch in the right margin */}
        <div
          aria-hidden
          className="hidden lg:flex absolute right-12 top-24 flex-col items-center gap-3 text-sumi/25 font-serif text-xs tracking-widest select-none pointer-events-none"
          style={{ writingMode: "vertical-rl" }}
        >
          <span>漢字を読む</span>
          <span className="h-8 w-px bg-sumi/15" />
          <span>意味を知る</span>
        </div>
        {/* Hanko-style seal — small red square with a stamp character */}
        <div
          aria-hidden
          className="hidden md:flex absolute right-12 lg:right-32 top-16 w-14 h-14 items-center justify-center border-[2.5px] border-shu/80 text-shu font-serif text-xl rotate-[-4deg] select-none pointer-events-none"
          style={{ backgroundColor: "rgba(199,89,73,0.04)" }}
        >
          建
        </div>

        <div className="max-w-5xl relative">
          {/* ── Brand mark / logo lockup ───────────────────────────────
              Color carries the meaning here: 漢 (the kanji concept)
              in sumi as the foundation, 字 (the character itself) in
              shu as the active half — the same palette the rest of
              the app uses. No diagrammatic connectors; the type
              speaks. Sized boldly so it reads as a real brand mark. */}
          <svg
            data-testid="welcome-logo"
            viewBox="0 0 380 175"
            className="block w-72 md:w-[420px] h-auto mb-6 -ml-2"
            aria-label="Kanji Build"
          >
            {/* primary mark — 漢字 (Han + character) with split colors */}
            <text
              x="0" y="118"
              fontFamily="serif" fontSize="120"
              fontWeight="500" fill="#1B1B1B"
            >
              漢
            </text>
            <text
              x="142" y="118"
              fontFamily="serif" fontSize="120"
              fontWeight="500" fill="#C75949"
            >
              字
            </text>

            {/* shu underline — anchors the typographic block */}
            <line x1="0" y1="138" x2="280" y2="138" stroke="#C75949" strokeWidth="2.5" />

            {/* "KANJI BUILD" — english lockup, sumi for legibility */}
            <text
              x="0" y="163"
              fontFamily="sans-serif" fontSize="15"
              fontWeight="700" letterSpacing="6"
              fill="#1B1B1B"
            >
              KANJI BUILD
            </text>
          </svg>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sumi leading-tight mb-8 max-w-4xl">
            How kanji relate to each other —{" "}
            <span className="text-shu">semantic, visual, phonetic</span>
          </h1>
          <p className="font-serif text-base md:text-lg text-sumi/80 leading-relaxed max-w-3xl mb-10">
            Kanji Build is designed to investigate how different kanji relate to each
            other — semantic, visual and phonetic relationships that help to understand
            not what individual kanji mean but how the body of kanji has evolved and
            works together. Learn new kanji organically by how it relates to what you
            already know and why it is related.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={startDemo}
              data-testid="welcome-start-demo"
              className="inline-flex items-center gap-2 bg-sumi text-washi px-6 py-3 font-sans text-sm uppercase tracking-[0.25em] hover:bg-shu transition-colors"
            >
              Take the guided tour
              <ArrowRight size={14} />
            </button>
            {!user && (
              <button
                type="button"
                onClick={signIn}
                data-testid="welcome-sign-in"
                className="inline-flex items-center gap-2 px-6 py-3 font-sans text-sm uppercase tracking-[0.25em] text-sumi border border-borderDefault hover:border-shu hover:text-shu transition-colors"
              >
                Sign in
              </button>
            )}
            <PWAInstallButton />
          </div>

          {/* Secondary text link — for the reader-first crowd. Positioned
              directly under the CTAs so it can't be missed but styled as
              a quiet supporting affordance, not another button. */}
          <p className="mt-4 font-sans text-xs md:text-sm text-sumiSoft">
            Prefer to read first?{" "}
            <a
              href="/guide"
              data-testid="welcome-read-guide"
              className="text-shu underline underline-offset-4 decoration-shu/40 hover:decoration-shu transition-colors"
            >
              Read the user guide →
            </a>
          </p>

          <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mt-8">
            2,800 kanji · 231 phonetic drivers · 382 productive radicals
          </p>
        </div>
      </section>

      {/* ── Decorative kanji band ───────────────────────────────────── */}
      <section className="border-y border-borderDefault py-10 md:py-14 bg-washi/60 overflow-hidden relative">
        {/* Stylised Mt Fuji silhouette. Now bolder + bigger. */}
        <svg
          aria-hidden
          viewBox="0 0 400 120"
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[720px] md:w-[1080px] lg:w-[1320px] text-shu opacity-[0.16] pointer-events-none"
        >
          <path
            d="M0 110 L130 110 L175 38 L195 50 L215 30 L235 55 L255 22 L275 60 L295 38 L400 110 Z"
            fill="currentColor"
          />
          {/* Snow cap — solid washi to read like real snow */}
          <path
            d="M255 22 L235 55 L223 47 L215 40 L207 50 L215 30 Z"
            fill="#FAF5EB"
          />
          {/* Sun behind the peak */}
          <circle cx="295" cy="38" r="22" fill="#C75949" opacity="0.55" />
        </svg>
        {/* 富士 kanji woven into the composition — a real graphic, not a tag */}
        <span
          aria-hidden
          className="hidden md:block absolute right-[6%] lg:right-[12%] bottom-2 font-serif text-7xl lg:text-8xl text-sumi/30 leading-none select-none pointer-events-none tracking-[-0.05em]"
        >
          富士
        </span>
        {/* 寺 lives at the centre. Seven kanji built FROM 寺 fan out
            around it — six share the on'yomi じ. The geometry says
            "Learn 1, unlock 7" without needing a sentence to explain
            it. Satellites are placed on a circle of radius 110 from
            the centre (200,140), evenly spaced 360/7 starting at the
            top so 時 (the demo kanji) sits at 12 o'clock. */}
        <div className="flex flex-col items-center max-w-3xl mx-auto px-6 relative">
          <svg
            data-testid="welcome-temple-family"
            viewBox="0 0 400 295"
            className="w-full max-w-md md:max-w-lg"
            aria-label="The kanji 寺 with seven kanji built from it"
          >
            {/* connector lines from centre to each satellite */}
            <g stroke="rgba(199,89,73,0.40)" strokeWidth="1">
              <line x1="200" y1="140" x2="200" y2="30" />
              <line x1="200" y1="140" x2="286" y2="71" />
              <line x1="200" y1="140" x2="307" y2="164" />
              <line x1="200" y1="140" x2="248" y2="239" />
              <line x1="200" y1="140" x2="152" y2="239" />
              <line x1="200" y1="140" x2="93" y2="164" />
              <line x1="200" y1="140" x2="114" y2="71" />
            </g>

            {/* satellites — kanji built from 寺 */}
            {[
              { ch: "時", x: 200, y: 30,  label: "time" },
              { ch: "持", x: 286, y: 71,  label: "hold" },
              { ch: "特", x: 307, y: 164, label: "special" },
              { ch: "待", x: 248, y: 239, label: "wait" },
              { ch: "等", x: 152, y: 239, label: "equal" },
              { ch: "詩", x: 93,  y: 164, label: "poetry" },
              { ch: "侍", x: 114, y: 71,  label: "samurai" },
            ].map(({ ch, x, y, label }) => (
              <g key={ch}>
                <circle
                  cx={x} cy={y} r="24"
                  fill="#F4F1EB"
                  stroke="rgba(27,27,27,0.45)"
                  strokeWidth="1"
                />
                <text
                  x={x} y={y + 10}
                  textAnchor="middle"
                  fontFamily="serif"
                  fontSize="26"
                  fill="#1B1B1B"
                >
                  {ch}
                </text>
                <text
                  x={x} y={y + 42}
                  textAnchor="middle"
                  fontFamily="sans-serif"
                  fontSize="8"
                  fill="rgba(27,27,27,0.85)"
                  letterSpacing="1.5"
                  style={{ textTransform: "uppercase" }}
                >
                  {label}
                </text>
              </g>
            ))}

            {/* centre — 寺, larger circle with shu border to signal the root */}
            <circle
              cx="200" cy="140" r="40"
              fill="#F4F1EB"
              stroke="#C75949"
              strokeWidth="2.5"
            />
            <text
              x="200" y="156"
              textAnchor="middle"
              fontFamily="serif"
              fontSize="48"
              fill="#1B1B1B"
            >
              寺
            </text>
          </svg>
          <p className="text-center font-sans text-xs md:text-sm uppercase tracking-[0.35em] text-shu mt-4 md:mt-6">
            Learn 1 kanji, unlock 7
          </p>
          <p className="text-center font-serif text-sumi/75 text-sm md:text-base mt-3 max-w-lg leading-relaxed">
            寺 (temple) lives inside seven other kanji — and six of them also
            inherit its on&apos;yomi <span className="text-shu">じ</span>. One
            building block, a whole family revealed.
          </p>
        </div>
      </section>

      {/* ── Three pillars ──────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24 relative overflow-hidden">
        {/* Bold 桜 kanji woven into a drift of petals on the right. */}
        <Sakura className="absolute top-6 right-6 md:right-10 w-40 md:w-56 text-shu/30 -rotate-12 pointer-events-none" />
        <Sakura className="absolute top-44 right-44 md:right-60 w-20 md:w-28 text-shu/20 rotate-12 pointer-events-none" />
        <Sakura className="absolute bottom-20 left-4 md:left-12 w-32 md:w-44 text-shu/25 rotate-[18deg] pointer-events-none" />
        <Sakura className="absolute bottom-44 left-44 md:left-60 w-16 md:w-20 text-shu/15 -rotate-[24deg] pointer-events-none" />
        <span
          aria-hidden
          className="hidden md:block absolute top-10 right-[14%] font-serif text-7xl lg:text-8xl text-shu/40 leading-none select-none pointer-events-none"
        >
          桜
        </span>
        <div className="max-w-6xl mx-auto relative">
          <h2 className="font-serif text-3xl md:text-4xl text-sumi mb-6">
            One kanji, three lenses
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
            <Pillar
              icon={<Layers size={22} strokeWidth={1.5} />}
              testid="welcome-pillar-decomposition"
              kanji="海"
              parts="氵 + 毎"
              title="Visual decomposition"
              copy="Every kanji broken into its parts, with stroke-by-stroke colour matching. Hover any part to see exactly which strokes belong to it — including positional radical variants like 氵 (water side-form) that link straight to 水's card."
            />
            <Pillar
              icon={<Network size={22} strokeWidth={1.5} />}
              testid="welcome-pillar-phonetic"
              kanji="寺"
              parts="→ 時 持 侍 詩"
              title="Phonetic families"
              copy="When 寺 appears inside a kanji the reading is almost always じ. Kanji Build tracks 231 such phonetic drivers — including five 'ghost' drivers (𢦏, 𠦝, 𠬶, 俞, 龹) that aren't in any modern dictionary but still predict the reading of 17 kanji."
            />
            <Pillar
              icon={<Compass size={22} strokeWidth={1.5} />}
              testid="welcome-pillar-radicals"
              kanji="氵"
              parts="115 kanji"
              title="Radical trees"
              copy="Every productive radical, ranked by how many kanji it grows into. Click 氵 to land on 水's card. Variant glyphs auto-merge into their parent so 水's 'grows into' shows every kanji built from 氵 — 海, 河, 池, 流, 江, 油, 波, 清."
            />
          </div>
        </div>
      </section>

      {/* ── Companion strip ────────────────────────────────────────── */}
      <section className="border-y border-borderDefault py-16 md:py-24 bg-washi/60 relative overflow-hidden">
        {/* Subtle watermark kanji in the background — decorative only. */}
        <span aria-hidden className="absolute -left-6 top-2 font-serif text-[10rem] md:text-[14rem] text-sumi/[0.04] select-none leading-none pointer-events-none">
          詩
        </span>
        <span aria-hidden className="absolute -right-6 bottom-2 font-serif text-[10rem] md:text-[14rem] text-sumi/[0.04] select-none leading-none pointer-events-none">
          時
        </span>
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-sumi leading-relaxed">
            Perfectly complements your existing flashcard apps, books or class
            by deepening your understanding of what you learn.
          </p>
        </div>
      </section>

      {/* ── Pricing / Welcome-back ────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 relative overflow-hidden">
        {/* Bold torii gate silhouette as the section backdrop. */}
        <Torii className="absolute left-1/2 -translate-x-1/2 top-8 w-[680px] md:w-[860px] lg:w-[980px] text-shu/25 pointer-events-none" />
        {/* 鳥居 written across the top beam — large, integral to the artwork. */}
        <span
          aria-hidden
          className="hidden md:block absolute left-1/2 -translate-x-1/2 top-1 font-serif text-3xl lg:text-4xl tracking-[0.4em] text-shu/55 pointer-events-none select-none"
        >
          鳥居
        </span>
        {lifetime ? (
          // Paid + logged in → handled by the cold-launch shortcut above.
          // Paid + NOT logged in → top sign-in strip is what they need;
          // this card just gives a second, very obvious entry point.
          <div className="max-w-md mx-auto text-center border border-shu p-10 md:p-12 bg-washi relative shadow-[0_0_80px_-10px_rgba(199,89,73,0.35)]">
            <button
              type="button"
              onClick={user
                ? () => {
                    try { localStorage.setItem("kb_seen_app_v2", "1"); } catch (e) {
                      console.warn("kb_seen_app_v2 persist failed (private browsing?)", e);
                    }
                    navigate("/");
                  }
                : signIn}
              data-testid="welcome-enter-app"
              className="w-full bg-sumi text-washi px-6 py-4 font-sans text-base uppercase tracking-[0.25em] hover:bg-shu transition-colors"
            >
              {user ? "Enter the app →" : "Login with Google →"}
            </button>
          </div>
        ) : (
          <div id="welcome-pricing" className="max-w-md mx-auto text-center border border-borderDefault p-10 md:p-12 bg-washi relative shadow-[0_0_60px_-15px_rgba(199,89,73,0.25)] scroll-mt-20">
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumi mb-3">
              one payment
            </p>
            <h3 className="font-serif text-6xl md:text-7xl text-sumi leading-none mb-2">£20</h3>
            <p className="font-serif text-xl md:text-2xl text-sumi mb-8">
              lifetime access
            </p>
            <ul className="text-left font-sans text-sm text-sumi space-y-3 mb-8">
              <Bullet>All 2,800 kanji incl. jinmeiyou — every connection</Bullet>
              <Bullet>Phonetic Atlas (231 drivers, 445 bonds)</Bullet>
              <Bullet>Radical browser with full member lists</Bullet>
              <Bullet>Journal &amp; study mode</Bullet>
              <Bullet>Offline-capable — install as an app on any device</Bullet>
            </ul>
            <button
              type="button"
              onClick={() => startCheckout.mutate()}
              disabled={startCheckout.isPending}
              data-testid="welcome-pricing-buy"
              className="w-full bg-shu text-washi px-6 py-3 font-sans text-sm uppercase tracking-[0.25em] hover:bg-sumi transition-colors disabled:opacity-50 mb-3"
            >
              {startCheckout.isPending ? "Redirecting…" : "Unlock for £20"}
            </button>
            <button
              type="button"
              onClick={startDemo}
              data-testid="welcome-pricing-demo"
              className="w-full px-6 py-3 font-sans text-xs uppercase tracking-[0.25em] text-sumi border border-borderDefault hover:border-shu hover:text-shu transition-colors"
            >
              Try the demo first
            </button>
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft mt-6 leading-relaxed">
              one payment · lifetime access · 14-day refund
            </p>
          </div>
        )}
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-borderDefault py-8 px-6 md:px-12 lg:px-24 flex items-center justify-between flex-wrap gap-4">
        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft">
          漢字 build · investigate, don&apos;t memorise
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          <a
            href="/privacy"
            data-testid="welcome-footer-privacy"
            className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft hover:text-shu"
          >
            privacy
          </a>
          <a
            href="/terms"
            data-testid="welcome-footer-terms"
            className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft hover:text-shu"
          >
            terms
          </a>
          <a
            href="/guide"
            data-testid="welcome-footer-guide"
            className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumi hover:text-shu"
          >
            user guide
          </a>
          <button
            type="button"
            onClick={startDemo}
            data-testid="welcome-footer-demo"
            className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumi hover:text-shu"
          >
            take the tour →
          </button>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ icon, testid, kanji, parts, title, copy }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      data-testid={testid}
      className="border border-borderDefault bg-washi p-6 md:p-8 hover:border-shu/60 transition-colors"
    >
      <div className="flex items-baseline justify-between mb-5">
        <span className="font-serif text-6xl text-sumi leading-none">{kanji}</span>
        <span className="text-shu">{icon}</span>
      </div>
      <p className="font-sans text-xs uppercase tracking-[0.25em] text-sumiSoft mb-3">
        {parts}
      </p>
      <h3 className="font-serif text-2xl text-sumi mb-4">{title}</h3>
      <p className="font-sans text-base text-sumi leading-relaxed">{copy}</p>
    </motion.article>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3">
      <span className="text-shu font-serif text-base leading-none">·</span>
      <span>{children}</span>
    </li>
  );
}

/* ── Cultural motifs ───────────────────────────────────────────────────
 * Tiny inline SVGs. Each takes its colour from currentColor so the parent
 * can tune opacity / hue via Tailwind classes. Stroke-only and
 * unaspirated — they read as quiet hand-drawn marks, never illustrations.
 * ──────────────────────────────────────────────────────────────────── */

function Sakura({ className }) {
  // Five-petal cherry blossom. Each petal is a rounded teardrop.
  const petal = "M0 -28 C 8 -22, 12 -8, 0 0 C -12 -8, -8 -22, 0 -28 Z";
  return (
    <svg
      aria-hidden
      viewBox="-40 -40 80 80"
      className={className}
      fill="currentColor"
    >
      {[0, 72, 144, 216, 288].map((deg) => (
        <path key={deg} d={petal} transform={`rotate(${deg})`} />
      ))}
      {/* centre */}
      <circle r="3" fill="currentColor" opacity="0.6" />
    </svg>
  );
}

function Torii({ className }) {
  // Stylised torii gate (鳥居). Two top beams, two vertical pillars,
  // and a small horizontal tie-beam. Stroke-only.
  return (
    <svg
      aria-hidden
      viewBox="0 0 200 140"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      {/* Top kasagi beam (curved up at the ends) */}
      <path d="M14 22 Q 100 8 186 22" />
      {/* Second beam (shimaki) — straight, just below */}
      <path d="M22 36 L178 36" />
      {/* Tie-beam (nuki) across the pillars */}
      <path d="M30 70 L170 70" />
      {/* Left pillar */}
      <path d="M44 36 L44 130" />
      {/* Right pillar */}
      <path d="M156 36 L156 130" />
    </svg>
  );
}

